import { useEffect, useState } from "react";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, X, Wrench } from "lucide-react";

// Penyesuaian unit ber-IMEI: rusak / hilang / pulih / ditemukan, dua tahap
// (brief owner 4 Sep 2026 P14 + gugus D). Disisipkan SATU baris di tab "Unit & Riwayat
// IMEI" InventoryStok.jsx — dibuat terpisah supaya berkas halaman itu (yang
// juga disunting agent transfer) tidak bertabrakan saat digabung.
//
// Gerbang tombol memakai kunci yang SAMA dengan backend
// (`routers/inventory/penyesuaian_unit.py`):
// - Ajukan  : `inventory_opname.create` (require_permission di POST).
// - Setujui/Tolak : `_boleh_putuskan_opname` — role lama ATAU izin EKSPLISIT
//   `inventory_opname.update`; saklar induk `inventory` SENGAJA tidak dihitung,
//   persis cermin di FinancePersediaan.jsx. Memakai `can("inventory_opname",
//   "update")` polos akan menampilkan tombol untuk finance_staff (bawaan role)
//   lalu berakhir 403 — dilarang aturan #3 CLAUDE.md.
const ROLE_PEMUTUS = ["superadmin", "admin", "finance_spv", "finance_ceo"];

const TINDAKAN = {
  rusak: { l: "Rusak", ket: "Unit tetap di gudang & tetap bernilai, tidak bisa dijual. Tanpa jurnal.", dari: ["tersedia"] },
  hilang: { l: "Hilang", ket: "Dihapusbukukan: lapisan modal & kartu stok dikurangi, jurnal D 6900 / K persediaan.", dari: ["tersedia", "rusak"] },
  pulih: { l: "Pulih", ket: "Unit rusak yang sudah diperbaiki kembali bisa dijual. Tanpa jurnal.", dari: ["rusak"] },
  // Brief 4 Sep 2026 gugus D. Barang hilang yang ketemu lagi HARUS lewat sini,
  // bukan lewat PO — PO akan melahirkan lapisan modal kedua untuk satu barang
  // fisik, dan backend memang menolaknya.
  ditemukan: {
    l: "Ditemukan",
    ket: "Unit hilang yang ketemu lagi: lapisan modal & kartu stok masuk kembali senilai yang dulu dihapusbukukan, jurnal D persediaan / K 6900. Hanya di gudang asal unit.",
    dari: ["hilang"],
  },
};
// Tindakan yang menggerakkan uang — menentukan kolom "Nilai" terisi atau "—",
// dan apakah catatan jurnal ditampilkan. Ditulis sekali di sini supaya tidak
// ada tempat kedua yang lupa menambahkan "ditemukan".
const TINDAKAN_BERUANG = ["hilang", "ditemukan"];

const STATUS_DOK = {
  pending: { l: "Menunggu", c: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { l: "Disetujui", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { l: "Ditolak", c: "bg-rose-50 text-rose-700 border-rose-200" },
};

const inp = "h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

export default function PenyesuaianUnitPanel({ units = [], modalTerlihat = false, onSelesai }) {
  const { can, user } = useAuth();
  const bolehAjukan = can("inventory_opname", "create");
  const bolehPutuskan = ROLE_PEMUTUS.includes(user?.role)
    || user?.permissions?.inventory_opname?.update === true;
  // Daftar pengajuan boleh dilihat pengaju, pemegang view, atau pemutus —
  // cermin `_boleh_lihat_penyesuaian` di backend.
  const bolehLihat = can("inventory_opname", "view") || bolehAjukan || bolehPutuskan;

  const [daftar, setDaftar] = useState([]);
  const [memuat, setMemuat] = useState(false);
  const [buka, setBuka] = useState(false);
  const [form, setForm] = useState({ tindakan: "rusak", alasan: "", unitIds: [] });
  const [busy, setBusy] = useState(false);
  const [tolak, setTolak] = useState(null); // {id, code, alasan}
  const [rinci, setRinci] = useState(null); // id pengajuan yang item-nya dibentangkan

  const muat = async () => {
    if (!bolehLihat) return;
    setMemuat(true);
    try {
      const r = await api.get("/inventory/penyesuaian-unit", { params: { limit: 50 } });
      setDaftar(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat pengajuan penyesuaian unit");
    } finally { setMemuat(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { muat(); }, [bolehLihat]);

  if (!bolehLihat) return null;

  // Unit yang bisa dicentang: statusnya memenuhi syarat tindakan yang dipilih.
  // Satu pengajuan = SATU gudang (aturan backend), jadi begitu unit pertama
  // dicentang, unit di gudang lain dinonaktifkan — bukan ditolak diam-diam
  // saat submit.
  const syarat = TINDAKAN[form.tindakan].dari;
  const kandidat = units.filter(u => syarat.includes(u.status));
  const terpilih = kandidat.filter(u => form.unitIds.includes(u.id));
  const gudangTerpilih = terpilih[0]?.warehouse_id || null;

  const toggleUnit = (u) => {
    setForm(f => f.unitIds.includes(u.id)
      ? { ...f, unitIds: f.unitIds.filter(x => x !== u.id) }
      : { ...f, unitIds: [...f.unitIds, u.id] });
  };
  const gantiTindakan = (t) => setForm({ tindakan: t, alasan: form.alasan, unitIds: [] });

  const ajukan = async () => {
    if (!terpilih.length) { toast.error("Pilih minimal satu unit"); return; }
    if (form.alasan.trim().length < 3) { toast.error("Alasan wajib diisi (minimal 3 karakter)"); return; }
    setBusy(true);
    try {
      const r = await api.post("/inventory/penyesuaian-unit", {
        warehouse_id: gudangTerpilih, tindakan: form.tindakan,
        alasan: form.alasan.trim(), unit_ids: terpilih.map(u => u.id),
      });
      toast.success(r.data.message || "Pengajuan disimpan");
      setForm({ tindakan: form.tindakan, alasan: "", unitIds: [] });
      setBuka(false);
      await muat();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengajukan penyesuaian");
    } finally { setBusy(false); }
  };

  const setujui = async (d) => {
    const dampak = d.tindakan === "hilang"
      ? "\nUnit akan dihapusbukukan dan jurnal dibuat."
      : d.tindakan === "ditemukan"
        ? "\nUnit kembali tersedia, modalnya masuk lagi ke persediaan dan jurnal dibuat."
        : "";
    if (!window.confirm(`Setujui ${d.code} (${TINDAKAN[d.tindakan]?.l || d.tindakan}, ${d.jumlah_unit ?? (d.items || []).length} unit)?` + dampak)) return;
    setBusy(true);
    try {
      const r = await api.post(`/inventory/penyesuaian-unit/${d.id}/approve`);
      toast.success(r.data.message || "Disetujui");
      await muat();
      if (onSelesai) await onSelesai();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyetujui");
    } finally { setBusy(false); }
  };

  const kirimTolak = async () => {
    if (!tolak || tolak.alasan.trim().length < 3) { toast.error("Alasan penolakan minimal 3 karakter"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/inventory/penyesuaian-unit/${tolak.id}/tolak`, { alasan: tolak.alasan.trim() });
      toast.success(r.data.message || "Ditolak");
      setTolak(null);
      await muat();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menolak");
    } finally { setBusy(false); }
  };

  const menunggu = daftar.filter(d => d.status === "pending");

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3.5 space-y-3" data-testid="psu-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1.5">
          <Wrench className="w-4 h-4 text-slate-500" /> Penyesuaian Unit (rusak / hilang / pulih / ditemukan)
        </p>
        {menunggu.length > 0 && (
          <span className="text-[11px] px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
            {menunggu.length} menunggu keputusan
          </span>
        )}
        <div className="flex-1" />
        {bolehAjukan && (
          <button onClick={() => setBuka(b => !b)} data-testid="psu-buka-form"
                  className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700">
            {buka ? "Tutup form" : "Ajukan penyesuaian"}
          </button>
        )}
      </div>

      {buka && bolehAjukan && (
        <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50/60" data-testid="psu-form">
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">Tindakan</label>
              <select value={form.tindakan} onChange={e => gantiTindakan(e.target.value)}
                      data-testid="psu-tindakan" className={inp}>
                {Object.entries(TINDAKAN).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[16rem]">
              <label className="block text-[11px] font-medium text-slate-500 mb-1">Alasan (wajib)</label>
              <input value={form.alasan} onChange={e => setForm(f => ({ ...f, alasan: e.target.value }))}
                     data-testid="psu-alasan" placeholder="mis. LCD pecah saat display; tidak ditemukan saat opname"
                     className={`${inp} w-full`} />
            </div>
            <button onClick={ajukan} disabled={busy || !terpilih.length} data-testid="psu-ajukan"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
              {busy ? "Menyimpan..." : `Ajukan (${terpilih.length} unit)`}
            </button>
          </div>
          <p className="text-[11px] text-slate-500">{TINDAKAN[form.tindakan].ket} Hanya unit berstatus {syarat.join("/")} dari daftar di bawah yang bisa dipilih; satu pengajuan = satu gudang.</p>
          {kandidat.length === 0 ? (
            <p className="text-sm text-slate-400">
              Tidak ada unit berstatus {syarat.join("/")} di hasil filter saat ini — ubah filter di atas lalu Terapkan
              {form.tindakan === "ditemukan" ? " (klik chip status \"hilang\" di atas daftar unit)" : ""}.
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg bg-white divide-y divide-slate-100">
              {kandidat.map(u => {
                const dicentang = form.unitIds.includes(u.id);
                const bedaGudang = gudangTerpilih && u.warehouse_id !== gudangTerpilih;
                return (
                  <label key={u.id} className={`flex items-center gap-3 px-3 py-1.5 text-sm ${bedaGudang ? "opacity-40" : "hover:bg-slate-50 cursor-pointer"}`}>
                    <input type="checkbox" checked={dicentang} disabled={!!bedaGudang}
                           onChange={() => toggleUnit(u)} data-testid={`psu-unit-${u.identifier}`}
                           className="w-4 h-4 rounded border-slate-300" />
                    <span className="font-mono text-xs text-emerald-800">{u.identifier}</span>
                    <span className="truncate flex-1">{u.nama}</span>
                    <span className="text-xs text-slate-500">{u.warehouse_nama}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200">{u.status}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {memuat ? (
        <p className="text-sm text-slate-400">Memuat pengajuan...</p>
      ) : daftar.length === 0 ? (
        <p className="text-sm text-slate-400">Belum ada pengajuan penyesuaian unit.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold">Nomor / Tanggal</th>
                <th className="px-3 py-2 font-semibold">Gudang</th>
                <th className="px-3 py-2 font-semibold">Tindakan</th>
                <th className="px-3 py-2 font-semibold text-right">Unit</th>
                {modalTerlihat && <th className="px-3 py-2 font-semibold text-right">Nilai</th>}
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Diajukan</th>
                <th className="px-3 py-2 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {daftar.map(d => {
                const st = STATUS_DOK[d.status] || STATUS_DOK.pending;
                const n = d.jumlah_unit ?? (d.items || []).length;
                return [
                  <tr key={d.id} data-testid={`psu-row-${d.code}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2">
                      <button onClick={() => setRinci(r => r === d.id ? null : d.id)} className="font-mono text-xs text-emerald-800 underline decoration-dotted">
                        {d.code}
                      </button>
                      <p className="text-[11px] text-slate-500">{d.tanggal}</p>
                    </td>
                    <td className="px-3 py-2">{d.warehouse_nama}</td>
                    <td className="px-3 py-2">{TINDAKAN[d.tindakan]?.l || d.tindakan}</td>
                    <td className="px-3 py-2 text-right">{n}</td>
                    {modalTerlihat && (
                      <td className="px-3 py-2 text-right font-mono">
                        {TINDAKAN_BERUANG.includes(d.tindakan)
                          ? `${idr(d.nilai_total)}${d.perkiraan ? " (perkiraan)" : ""}` : "—"}
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded border ${st.c}`}>{st.l}</span>
                      {d.status === "rejected" && d.tolak_alasan && (
                        <p className="text-[11px] text-rose-600 mt-0.5">{d.tolak_alasan}</p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {d.diajukan_oleh_nama || d.diajukan_oleh}
                      <p className="text-[11px] text-slate-500 truncate max-w-[14rem]" title={d.alasan}>{d.alasan}</p>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {d.status === "pending" && bolehPutuskan && (
                        <>
                          <button onClick={() => setujui(d)} disabled={busy} data-testid={`psu-approve-${d.id}`}
                                  className="px-2.5 h-8 rounded hover:bg-emerald-50 text-emerald-700 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Setujui
                          </button>
                          <button onClick={() => setTolak({ id: d.id, code: d.code, alasan: "" })} disabled={busy} data-testid={`psu-tolak-${d.id}`}
                                  className="px-2.5 h-8 rounded hover:bg-rose-50 text-rose-600 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50">
                            <X className="w-3.5 h-3.5" /> Tolak
                          </button>
                        </>
                      )}
                      {d.status === "pending" && !bolehPutuskan && (
                        <span className="text-[11px] text-slate-400">menunggu pemutus</span>
                      )}
                    </td>
                  </tr>,
                  rinci === d.id && (
                    <tr key={`${d.id}-rinci`} className="bg-slate-50/60" data-testid={`psu-rinci-${d.code}`}>
                      <td colSpan={modalTerlihat ? 8 : 7} className="px-3 py-2">
                        <div className="flex flex-wrap gap-2">
                          {(d.items || []).map(it => (
                            <span key={it.unit_id} className="text-xs px-2 py-1 rounded border bg-white border-slate-200">
                              <span className="font-mono text-emerald-800">{it.identifier}</span> · {it.nama}
                              {it.status_sebelum ? ` · dari ${it.status_sebelum}` : ""}
                              {modalTerlihat && it.harga_perolehan != null ? ` · ${idr(it.harga_perolehan)}` : ""}
                            </span>
                          ))}
                        </div>
                        {d.status === "approved" && TINDAKAN_BERUANG.includes(d.tindakan) && (
                          <p className="text-[11px] text-slate-500 mt-1.5 inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {d.journal_txn_id
                              ? (d.tindakan === "hilang"
                                ? "Jurnal D 6900 / K persediaan sudah diposting."
                                : "Jurnal D persediaan / K 6900 sudah diposting — modal unit masuk lagi.")
                              : (d.jurnal_catatan || "Tanpa jurnal.")}
                          </p>
                        )}
                        {/* Pengajuan yang GAGAL di tengah meninggalkan catatan
                            kompensasi manual di field yang sama; tanpa
                            menampilkannya, satu-satunya jejaknya cuma log
                            server yang tidak dibuka siapa pun. */}
                        {d.status !== "approved" && d.jurnal_catatan && (
                          <p className="text-[11px] text-rose-600 mt-1.5 inline-flex items-center gap-1"
                             data-testid={`psu-catatan-${d.code}`}>
                            <AlertTriangle className="w-3 h-3" /> {d.jurnal_catatan}
                          </p>
                        )}
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      {tolak && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setTolak(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-4 space-y-3" onClick={e => e.stopPropagation()} data-testid="psu-tolak-modal">
            <p className="font-semibold text-slate-900">Tolak {tolak.code}</p>
            <input autoFocus value={tolak.alasan} onChange={e => setTolak(t => ({ ...t, alasan: e.target.value }))}
                   placeholder="Alasan penolakan (wajib)" data-testid="psu-tolak-alasan" className={`${inp} w-full`} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setTolak(null)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600">Batal</button>
              <button onClick={kirimTolak} disabled={busy} data-testid="psu-tolak-kirim"
                      className="h-9 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Mengirim..." : "Tolak pengajuan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
